import {
  ACADEMIC_TERMS,
  AcademicEnrollmentType,
  currentAcademicYear,
  graduationYearOptions,
  withSavedYear,
} from '@backrs_ui/common-ui';

import {
  ProfileValueOptionResponse,
  UserProfileResponse,
  ProfileFields,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

/**
 *
 * This function will filter out any values you do not want to appear in the profile edit page.
 */
export const filterEditProfileProfileOptions = (
  profileOptions: ProfileValueOptionResponse | undefined,
): ProfileValueOptionResponse | undefined => {
  if (!profileOptions) return undefined;

  // Create a new object with the same structure
  const filteredOptions: ProfileValueOptionResponse = {
    ...profileOptions,
    // Filter academicEnrollmentTypes to only include IDs 2, 3, 4, 5
    academicEnrollmentTypes: profileOptions.academicEnrollmentTypes.filter((option) => {
      const allowedIds = [1, 2, 3, 4, 5, 6];

      return allowedIds.includes(option.id);
    }),
  };

  return filteredOptions;
};

export const getEnrollmentIds = (
  options: Record<string, ProfileValueOptionResponse[ProfileFields]> | undefined,
  desiredLevel: AcademicEnrollmentType,
) => {
  const academicEnrollmentTypes = options?.academicEnrollmentTypes;

  if (!academicEnrollmentTypes || !Array.isArray(academicEnrollmentTypes)) return [];

  return academicEnrollmentTypes
    .filter(
      (opt): opt is typeof opt & { level: AcademicEnrollmentType } =>
        'level' in opt && opt.level === desiredLevel,
    )
    .map((opt) => opt.id);
};

export const isCurrentlyInEduLevel = (
  eduLevel: AcademicEnrollmentType,
  profile?: UserProfileResponse,
  options?: ProfileValueOptionResponse,
) => {
  if (!profile?.profile?.academicEnrollmentTypes || !options) return false;

  const eduLevelIds = getEnrollmentIds(options, eduLevel);

  return eduLevelIds.includes(profile.profile.academicEnrollmentTypes.id);
};

const HOW_MANY_YEARS_TO_SHOW_FORMER_SCHOOL_YEARS = 51; // 50 + 1 for current year

// A level the student has already finished can have started decades ago.
const HOW_MANY_START_YEARS_TO_SHOW = 52;

export const getGraduationYears = (
  eduLevel: AcademicEnrollmentType,
  profile?: UserProfileResponse,
  options?: ProfileValueOptionResponse,
) => {
  const currentYear = new Date().getFullYear();

  const isInEduLevel = isCurrentlyInEduLevel(eduLevel, profile, options);

  // A level already finished needs past years, not years still to come.
  const years = isInEduLevel
    ? graduationYearOptions()
    : Array.from(
        { length: HOW_MANY_YEARS_TO_SHOW_FORMER_SCHOOL_YEARS },
        (_, index) => currentYear - index,
      );

  const startYear = Number(
    eduLevel === 'high_school'
      ? profile?.profile.academicHighSchoolProgramStartYear
      : profile?.profile.academicPostSecondaryProgramStartYear,
  );
  const stored =
    eduLevel === 'high_school'
      ? profile?.profile.academicHighSchoolExpectedGraduationYear
      : profile?.profile.academicPostSecondaryExpectedGraduationYear;

  const narrowed = years.filter((year) => !startYear || year > startYear);

  return withSavedYear(narrowed, stored).map((year) => ({
    id: year,
    name: year.toString(),
    value: year.toString(),
  }));
};

export const PROGRAM_START_TERM_OPTIONS = ACADEMIC_TERMS.map((term, index) => ({
  id: index + 1,
  name: term,
  value: term,
}));

// Next year first — a program that hasn't started yet is a real state
export const getProgramStartYears = (
  eduLevel: AcademicEnrollmentType,
  profile?: UserProfileResponse,
) => {
  const graduationYear = Number(
    eduLevel === 'high_school'
      ? profile?.profile.academicHighSchoolExpectedGraduationYear
      : profile?.profile.academicPostSecondaryExpectedGraduationYear,
  );
  const stored =
    eduLevel === 'high_school'
      ? profile?.profile.academicHighSchoolProgramStartYear
      : profile?.profile.academicPostSecondaryProgramStartYear;

  const firstYear = currentAcademicYear() + 1;

  const narrowed = Array.from(
    { length: HOW_MANY_START_YEARS_TO_SHOW },
    (_, index) => firstYear - index,
  ).filter((year) => !graduationYear || year < graduationYear);

  return withSavedYear(narrowed, stored).map((year) => ({
    id: year,
    name: year.toString(),
    value: year.toString(),
  }));
};
